'use client';

import React, { useMemo } from "react";

interface PaginationProps {
  page: number;
  setPage: (p: number) => void;
  totalPages: number;
  isLoading: boolean;
}

export default function Pagination({ page, setPage, totalPages, isLoading }: PaginationProps) {
  const pagination = useMemo(() => {
    const pages: (number | string)[] = [];
    const siblings = 1;
    const left = Math.max(2, page - siblings);
    const right = Math.min(totalPages - 1, page + siblings);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  }, [page, totalPages]);

  const goToPage = (p: number) => {
    if (!isLoading && p >= 1 && p <= totalPages) setPage(p);
  };

  return (
    <nav className="flex justify-center gap-2 mt-12 flex-wrap" aria-label="Pagination">
      <button
        onClick={() => goToPage(page - 1)}
        disabled={page === 1 || isLoading}
        className={`px-3 py-1 rounded-lg font-semibold transition ${
          page === 1
            ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
        aria-label="Previous page"
      >
        Prev
      </button>

      {pagination.map((p, idx) =>
        typeof p === "number" ? (
          <button
            key={idx}
            onClick={() => goToPage(p)}
            disabled={isLoading}
            className={`px-3 py-1 rounded-lg font-semibold transition ${
              p === page
                ? "bg-blue-600 text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
            aria-current={p === page ? "page" : undefined}
            aria-label={`Go to page ${p}`}
          >
            {p}
          </button>
        ) : (
          <span
            key={idx}
            className="px-2 py-1 text-gray-400 dark:text-gray-500"
            aria-hidden="true"
          >
            {p}
          </span>
        )
      )}

      <button
        onClick={() => goToPage(page + 1)}
        disabled={page === totalPages || isLoading}
        className={`px-3 py-1 rounded-lg font-semibold transition ${
          page === totalPages
            ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
        aria-label="Next page"
      >
        Next
      </button>
    </nav>
  );
}
